import React, { FC } from 'react'

//* styles
import {
  RobotReleaseContainer,
  WebinarFinePrint,
  WebinarFormIntro,
  WebinarSpeakersTitle,
} from './style'

//* utils
import { LocaleCodes } from '../../../../constants/localization.constants'
import { renderMarketoOrIframe } from '../../../../utils/cn.utils'
import ImgUtil from '../../../../utils/img.utils'
import { correctSlugPrefix, isExternalURL } from '../../../../utils/link.utils'
import { findTarget } from '../../../../utils/microcopy.utils'
import { renderRichText } from '../../../../utils/richTextHandler.utils'
import { getFormIntroText, getHeroData } from './util'

//* components
import SEOTags from '../../../global/seoTags'
import WrapperImage from '../../../global/wrapperImage'
import { BodyContent, BodyOuter, MarketoFormContainer } from '../formPage'

type RobotReleaseProps = {
  webinarData: any
}

type WebinarContentProps = {
  webinarData: any
  availableLocales: any
  locale: string
  marketoFormData: any
  thankYouPageUrl: string
  path: string
}

export const RenderRobotRelease: FC<RobotReleaseProps> = ({ webinarData }) => {
  if (webinarData.robot.url) {
    const robotReleaseImg = (
      <WrapperImage
        src={ImgUtil.getResizedImageUrl(webinarData.robot.url, ``)}
        alt={webinarData.robot.alt}
        className="RobotRelease__Img-File"
        width={200}
        height={140}
        threshold={650}
      />
    )

    if (webinarData.robotReleaseLink) {
      const target = isExternalURL(webinarData.robot.releaseLink) ? '_blank' : '_self'

      return (
        <RobotReleaseContainer>
          <a href={webinarData.robot.releaseLink} target={target}>
            {robotReleaseImg}
          </a>
        </RobotReleaseContainer>
      )
    } else {
      return <RobotReleaseContainer>{robotReleaseImg}</RobotReleaseContainer>
    }
  } else {
    return null
  }
}

export const WebinarContent: FC<WebinarContentProps> = ({
  webinarData,
  availableLocales,
  locale,
  marketoFormData,
  thankYouPageUrl,
  path,
}) => {
  const formSuccessHandler = (form: any) => {
    if (window && window.hasOwnProperty('MktoForms2')) {
      form.onSuccess(() => {
        window.location.href = correctSlugPrefix(thankYouPageUrl)
        return false
      })
    }
  }

  let speakerText = ''
  const countryCode = locale.slice(3)
  const englishSpeakerText = `resource.${webinarData.category.number}.speakers.text`
  const globalSpeakerText = `resource.${countryCode}.${webinarData.category.number}.speakers.text`

  if (webinarData.microcopy.values) {
    speakerText = findTarget(
      locale === LocaleCodes.ENGLISH.code ? englishSpeakerText : globalSpeakerText,
      webinarData.microcopy.values,
    )
  }

  const hasEmbeddedEntryBlock: boolean = webinarData?.body?.content?.some(
    (item: any) => item.nodeType === 'embedded-entry-block',
  )

  return (
    <>
      <SEOTags
        seoMetadata={webinarData.seoMetadata}
        path={path}
        slug={webinarData.slug}
        locale={locale}
        availableLocales={availableLocales}
      />

      {getHeroData(webinarData)}

      <BodyOuter $formAlignToBody={true}>
        <div>
          <BodyContent
            className="AR-WP__Body"
            $isRobotRelease={webinarData.robot.url}
            $isWebinar={true}>
            <RenderRobotRelease webinarData={webinarData} />

            {hasEmbeddedEntryBlock && (
              <WebinarSpeakersTitle className="WebinarSpeakers__title">
                {speakerText}
              </WebinarSpeakersTitle>
            )}

            {webinarData.body && renderRichText(webinarData.body)}

            <WebinarFinePrint className="Webinar__Fine-Print">
              {webinarData.finePrint && renderRichText(webinarData.finePrint)}
            </WebinarFinePrint>
          </BodyContent>

          <MarketoFormContainer $formAlignToBody={true}>
            {(locale !== LocaleCodes.CHINESE.code || webinarData.cnFormOverride) && (
              <div>
                <WebinarFormIntro>
                  <p className="WebinarFormIntro">{getFormIntroText(webinarData, locale)}</p>
                </WebinarFormIntro>

                {renderMarketoOrIframe({
                  defaultLocale: locale,
                  marketoFormData: webinarData.formOverride || marketoFormData,
                  formSuccessHandler: formSuccessHandler,
                  marketoJsScript: webinarData.marketoJsScript,
                  cnFormOverride: webinarData.cnFormOverride,
                })}
              </div>
            )}
          </MarketoFormContainer>
        </div>
      </BodyOuter>
    </>
  )
}
